// src/components/Alerts.js

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Alerts.css';
import MessageModal from './MessageModal'; // Import the MessageModal component
import DiscordButton from './DiscordButton';
import { formatPhoneNumber, isValidPhoneNumber } from './alertsPhone';

// The exact wording of the consent shown below the checkbox. ParlorPass stores
// this with every signup so we can show later what somebody actually agreed to.
// Change the wording -> bump the version.
const CONSENT_TEXT_VERSION = '2026-09-v2';

// Cloudflare Turnstile. The site key is public by design — it is the SECRET, held on
// the Pages project, that makes a token mean anything, and /api/sms-signup checks it
// server-side before the signup goes anywhere.
const TURNSTILE_SITEKEY = '0x4AAAAAAE5yfvaNxmZ-a7W8';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

const Alerts = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    smsOptIn: false,
    siren: false,
    website: '', // honeypot: real people never see or fill this
  });

  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const [modalMessage, setModalMessage] = useState(''); // State to hold the modal message
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [signupSucceeded, setSignupSucceeded] = useState(false); // Show Discord nudge in the modal after a successful signup

  // Load Turnstile once and render the widget into the box below the form.
  useEffect(() => {
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current) return;
      if (turnstileWidgetId.current !== null) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        action: 'turnstile-spin-v1',
        callback: (token) => setTurnstileToken(token),
        // A token is single-use and expires; clear it so the form cannot submit a
        // stale one and get a confusing refusal.
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    } else {
      document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`).addEventListener('load', render);
    }

    return () => { cancelled = true; };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && turnstileWidgetId.current !== null) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  };

  const handlePhoneChange = (e) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setFormData({
      ...formData,
      phone: formattedPhoneNumber,
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setErrors({});
    setSignupSucceeded(false);

    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a 10-digit US or Canadian phone number, starting with the area code';
    }
    if (!formData.smsOptIn) {
      newErrors.smsOptIn = 'You must agree to receive SMS alerts';
    }
    if (!turnstileToken) {
      newErrors.turnstile = 'Please confirm you are a person';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Goes to our own /api/sms-signup relay, which adds the secret server-side
    // and forwards to ParlorPass. ParlorPass owns the subscriber list.
    let result;
    try {
      const response = await fetch('/api/sms-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          consent: formData.smsOptIn === true,
          womensEvents: formData.siren === true,
          consentTextVersion: CONSENT_TEXT_VERSION,
          turnstileToken,
          website: formData.website,
        }),
      });
      result = await response.json();
    } catch {
      setModalMessage('We could not reach the sign-up service. Please check your connection and try again.');
      setIsModalOpen(true);
      return;
    }

    // A well-formed sign-up always comes back ok. It deliberately does not say
    // whether the number was already on the list, so we never leak that either.
    if (result && result.ok === true) {
      setModalMessage('Thank you for signing up! You will now receive updates about Sacramento pinball events.');
      setSignupSucceeded(true);
      setFormData({ firstName: '', lastName: '', phone: '', smsOptIn: false, siren: false, website: '' });
      resetTurnstile();
    } else if (result && result.code === 'RATE_LIMITED') {
      setModalMessage('That is a few too many tries in a row. Please wait a little while and try again.');
    } else if (result && result.error) {
      setModalMessage(result.error);
    } else {
      setModalMessage('There was an error. Please try again.');
    }

    // Turnstile tokens are single-use: after ANY attempt the old one is spent, so
    // clear it or the next submit fails with a confusing refusal.
    if (!(result && result.ok === true)) resetTurnstile();

    // Open the modal
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  return (
    <div className="alerts-container">
      <h1>Sign Up for Pinball Event Alerts</h1>
      <p>Stay informed about the latest Sacramento pinball events by signing up for our text alerts. Simply enter your details below to receive updates.</p>

      
      <form id="signupForm" onSubmit={handleSubmit}>
        <label htmlFor="firstName">
          First Name <span className="required">*</span>
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          placeholder="Enter your first name"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
        {errors.firstName && <p className="error">{errors.firstName}</p>}

        <label htmlFor="lastName">
          Last Name <span className="required">*</span>
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          placeholder="Enter your last name"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
        {errors.lastName && <p className="error">{errors.lastName}</p>}

        <label htmlFor="phone">
          Phone Number <span className="required">*</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          autoComplete="tel-national"
          placeholder="Enter your phone number"
          value={formData.phone}
          onChange={handlePhoneChange}
          required
        />
        {errors.phone && <p className="error">{errors.phone}</p>}

        <label className="smsOptInLabel">
          <input
            type="checkbox"
            name="smsOptIn"
            checked={formData.smsOptIn}
            onChange={handleCheckboxChange}
            required
          />
          I would like to receive SMS alerts <span className="required">*</span>
        </label>
        {errors.smsOptIn && <p className="error">{errors.smsOptIn}</p>}
        <p className="consent-note">
          By checking the box you agree to get text messages from Capital Pinball Parlor about
          events and tournaments. How often we text varies. Message and data rates may apply.
          Reply STOP to stop, or HELP for help. See our{' '}
          <Link to="/privacy">Privacy &amp; Text Message Terms</Link>.
        </p>

        {/* Honeypot: hidden from people, irresistible to bots. Anything typed
            here makes ParlorPass quietly discard the signup. */}
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        />

        <label className="smsOptInLabel sirenOptInLabel">
          <input
            type="checkbox"
            name="siren"
            checked={formData.siren}
            onChange={handleCheckboxChange}
          />
          Women's event alerts **
        </label>

        <div className="turnstile-box" ref={turnstileRef} />
        {errors.turnstile && <p className="error">{errors.turnstile}</p>}

        <button type="submit">Sign Up</button>

        <p className="required-note"><span className="required">*</span> required</p>
        <p className="inclusion-note">** See the <a href="https://www.ifpapinball.com/inclusion/" target="_blank" rel="noopener noreferrer">IFPA Diversity & Inclusion Policy</a></p>
      </form>

      <MessageModal
        show={isModalOpen}
        onClose={closeModal}
        message={modalMessage}
      >
        {signupSucceeded && (
          <div className="alerts-discord-nudge">
            <p>While you're here — the rest of the conversation happens on Discord.</p>
            <DiscordButton size="sm">Join our Discord</DiscordButton>
          </div>
        )}
      </MessageModal>
    </div>
  );
};

export default Alerts;
